import type {ReactElement} from 'react';
import {useEffect, useState} from 'react';
import React from 'react';
import {DownloadPanelSkeletons} from '@Components/download-panel/components/download-panel-skeletons/download-panel-skeletons';
import type {PosterLoadObject} from '@PosterWhiteboard/poster/poster.types';
import type {FallbackProps} from 'react-error-boundary';
import {ErrorBoundary as ReactErrorBoundary} from 'react-error-boundary';
import {DownloadPanelContent} from '@Components/download-panel/components/download-panel-content/download-panel-content';
import styles from './download-panel.module.scss';
import {logClientError} from '@Libraries/log.library';
import {DownloadPanelErrorSection} from '@Components/download-panel/components/download-panel-error-section/download-panel-error-section';
import {loadPosterData} from '@PosterWhiteboard/poster/poster-init';
import {getPosterObjectFromPosterBackendObject} from '@PosterWhiteboard/poster/poster-backend';
import type {DownloadPageData, DownloadPanelDefaultOptions} from '@Components/download-panel/download-panel.types';
import {useGetWebDownloadPosterTypes} from '@Hooks/download-dialog/useGetWebDownloadPosterTypes';
import {AiChatImageDownloadPanelContent} from '@Components/download-panel/components/ai-chat-image-download-panel-content/ai-chat-image-download-panel-content';
import type {AiChatImageDownloadContext} from '@Components/ai-chat-window/lib/ai-chat-downloads.library';

export interface DownloadPanelProps {
  id: string;
  className?: string;
  posterHashedId?: string;
  posterObject?: PosterLoadObject;
  aiChatImageDownloadContext?: AiChatImageDownloadContext;
  isOpenedInModal?: boolean;
  isOpenedInEditor?: boolean;
  isOpenedInPrintShop?: boolean;
  defaultOptions?: DownloadPanelDefaultOptions;
  posterVersionId?: string;
  showOutdatedPosterInfoBox?: boolean;
  downloadPageOptions?: DownloadPageData;
}

export function DownloadPanel({
  className = '',
  showOutdatedPosterInfoBox = false,
  isOpenedInPrintShop = false,
  isOpenedInEditor = true,
  isOpenedInModal = false,
  ...props
}: DownloadPanelProps): ReactElement {
  const [posterObject, setPosterObject] = useState(props.posterObject);
  const {isLoading: isLoadingWebPosterTypes} = useGetWebDownloadPosterTypes();

  const getAndSetPosterObject = async (): Promise<void> => {
    if (!props.posterHashedId) {
      return;
    }
    const {posterBackendObject} = await loadPosterData(props.posterHashedId);
    const posterLoadObject = getPosterObjectFromPosterBackendObject(posterBackendObject);
    setPosterObject(posterLoadObject);
  };

  useEffect((): void => {
    if (!props.posterHashedId && props.posterObject) {
      setPosterObject(props.posterObject);
    }
  }, [props.posterObject]);

  useEffect((): void => {
    if (props.posterHashedId && props.posterHashedId !== posterObject?.hashedID) {
      void getAndSetPosterObject();
    }
  }, [props.posterHashedId, posterObject?.hashedID]);

  if (!posterObject && !props.posterHashedId && !props.aiChatImageDownloadContext) {
    throw new Error('expected poster object, poster hash id, or ai chat image download context to be provided');
  }

  const getSkeletons = (): ReactElement => {
    return (
      <div className={`_full-width _overflow-auto _full-height spacing-p-t-3 spacing-p-b-3 spacing-p-r-4 spacing-p-l-4 ${styles.container}`}>
        <DownloadPanelSkeletons isPanelOpenedInModal={isOpenedInModal} />
      </div>
    );
  };

  const isLoading = props.aiChatImageDownloadContext ? false : (!posterObject || isLoadingWebPosterTypes);

  const getContent = (): ReactElement => {
    if (isLoading) {
      return getSkeletons();
    }

    if (props.aiChatImageDownloadContext) {
      return (
        <AiChatImageDownloadPanelContent
          aiChatImageId={props.aiChatImageDownloadContext.aiChatImageId}
          chatId={props.aiChatImageDownloadContext.chatId}
          id={props.id}
          className={className}
          isOpenedInModal={isOpenedInModal}
        />
      );
    }

    if (!posterObject) {
        return getSkeletons();
    }

    return (
      <DownloadPanelContent
        isOpenedInPrintShop={isOpenedInPrintShop}
        isOpenedInEditor={isOpenedInEditor}
        isOpenedInModal={isOpenedInModal}
        posterObject={posterObject}
        id={props.id}
        className={className}
        defaultOptions={props.defaultOptions}
        posterVersionId={props.posterVersionId}
        downloadPageOptions={props.downloadPageOptions}
        showOutdatedPosterInfoBox={showOutdatedPosterInfoBox}
      />
    );
  };

  const onError = (e: Error): void => {
    void logClientError(e, {
      reason: 'DOWNLOAD_PANEL_ERROR_BOUNDARY',
    });
  };

  const getErrorFallback = ({error}: FallbackProps): ReactElement<unknown, typeof React.Component> => {
    return <DownloadPanelErrorSection errorMessage={error.message} />;
  };

  return (
    <ReactErrorBoundary fallbackRender={getErrorFallback} onError={onError}>
      {getContent()}
    </ReactErrorBoundary>
  );
}
